import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  ArchiveSceneMode,
  DirectorScenePayload,
  DirectorSubmitState,
  Id,
  ScriptDocument,
} from "@supermanus/document-model";
import {
  createDirectorArchive,
  deleteDirectorArchive,
  listDirectorArchives,
  listDirectorForks,
  upsertDirectorFork,
  type DirectorArchiveRow,
} from "./directorApi";
import {
  applyPayloadToScene,
  buildDirectorPayload,
  displayPayloadForFork,
  documentWithDirectorFork,
  hasActiveSubmission,
} from "./directorPayload";

const PUSH_DEBOUNCE_MS = 400;

function withSubmitState(
  payload: DirectorScenePayload,
  submitState: DirectorSubmitState | undefined,
): DirectorScenePayload {
  if (!submitState) {
    const { submitState: _drop, ...rest } = payload;
    return rest;
  }
  return { ...payload, submitState };
}

export function useDirectorWorkspace({
  enabled,
  apiBase,
  projectId,
  episodeId,
  document,
  onDocumentMutate,
}: {
  enabled: boolean;
  apiBase: string | null | undefined;
  projectId: Id;
  episodeId: Id | null;
  document: ScriptDocument;
  /** Persist mutations against the shared document (submitted vault scene). */
  onDocumentMutate?: (
    mutate: (doc: ScriptDocument) => ScriptDocument,
  ) => void;
}) {
  const [forks, setForks] = useState<Record<Id, DirectorScenePayload>>({});
  const [archivesByScene, setArchivesByScene] = useState<
    Record<Id, DirectorArchiveRow[]>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const forksRef = useRef(forks);
  forksRef.current = forks;
  const pushTimersRef = useRef<Record<string, number>>({});
  const documentRef = useRef(document);
  documentRef.current = document;
  const onDocumentMutateRef = useRef(onDocumentMutate);
  onDocumentMutateRef.current = onDocumentMutate;

  const clearPushTimers = useCallback(() => {
    for (const id of Object.keys(pushTimersRef.current)) {
      window.clearTimeout(pushTimersRef.current[id]);
    }
    pushTimersRef.current = {};
  }, []);

  const schedulePush = useCallback(
    (sourceSceneId: Id, payload: DirectorScenePayload) => {
      if (!apiBase || !episodeId) return;
      window.clearTimeout(pushTimersRef.current[sourceSceneId]);
      pushTimersRef.current[sourceSceneId] = window.setTimeout(() => {
        void upsertDirectorFork(
          apiBase,
          projectId,
          sourceSceneId,
          episodeId,
          payload,
        ).catch((err) => {
          console.error(err);
          setError(
            err instanceof Error
              ? err.message
              : "Kunne ikke gemme din version.",
          );
        });
      }, PUSH_DEBOUNCE_MS);
    },
    [apiBase, episodeId, projectId],
  );

  const pushNow = useCallback(
    async (sourceSceneId: Id, payload: DirectorScenePayload) => {
      if (!apiBase || !episodeId) return;
      window.clearTimeout(pushTimersRef.current[sourceSceneId]);
      await upsertDirectorFork(
        apiBase,
        projectId,
        sourceSceneId,
        episodeId,
        payload,
      );
    },
    [apiBase, episodeId, projectId],
  );

  useEffect(() => {
    if (!enabled || !apiBase || !episodeId) {
      setForks({});
      setArchivesByScene({});
      setError(null);
      setLoading(false);
      clearPushTimers();
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const rows = await listDirectorForks(apiBase, projectId, episodeId);
        if (cancelled) return;
        const next: Record<Id, DirectorScenePayload> = {};
        for (const row of rows) {
          next[row.sourceSceneId] = row.payload;
        }
        setForks(next);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Kunne ikke hente tilrettelæggermanus.",
          );
          setForks({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      clearPushTimers();
    };
  }, [apiBase, clearPushTimers, enabled, episodeId, projectId]);

  // Reconcile: if submitted vault scene was consumed by someone else, mark fork.
  useEffect(() => {
    if (!enabled) return;
    let changed = false;
    const next: Record<Id, DirectorScenePayload> = { ...forksRef.current };
    for (const [sourceSceneId, payload] of Object.entries(next)) {
      const state = payload.submitState;
      if (!state?.submittedSceneId || state.submittedConsumed) continue;
      if (hasActiveSubmission(document, sourceSceneId, payload)) continue;
      next[sourceSceneId] = withSubmitState(payload, {
        ...state,
        locked: true,
        submittedConsumed: true,
        submittedSceneId: null,
      });
      changed = true;
      schedulePush(sourceSceneId, next[sourceSceneId]!);
    }
    if (changed) setForks(next);
  }, [document, enabled, schedulePush]);

  const mergedDocument = useMemo(() => {
    if (!enabled) return document;
    let next = document;
    for (const [sceneId, payload] of Object.entries(forks)) {
      if (!document.scenes[sceneId]) continue;
      const display = displayPayloadForFork(document, sceneId, payload);
      next = documentWithDirectorFork(next, sceneId, display);
    }
    return next;
  }, [document, enabled, forks]);

  const hasFork = useCallback(
    (sceneId: Id) => Boolean(forks[sceneId]),
    [forks],
  );

  const isSubmitLocked = useCallback(
    (sceneId: Id) => Boolean(forks[sceneId]?.submitState?.locked),
    [forks],
  );

  const editsSubmittedVault = useCallback(
    (sceneId: Id) => {
      const payload = forks[sceneId];
      if (!payload?.submitState || payload.submitState.locked) return false;
      return hasActiveSubmission(document, sceneId, payload);
    },
    [document, forks],
  );

  const sceneRowClass = useCallback(
    (sceneId: Id) => {
      if (!enabled) return undefined;
      const payload = forks[sceneId];
      if (!payload) return "is-director-program";
      if (payload.submitState?.locked) return "is-director-submitted";
      if (
        payload.submitState &&
        hasActiveSubmission(document, sceneId, payload)
      ) {
        return "is-director-submitted-open";
      }
      return "is-director-own";
    },
    [document, enabled, forks],
  );

  const createFork = useCallback(
    async (sourceSceneId: Id) => {
      if (!apiBase || !episodeId) {
        throw new Error("API mangler");
      }
      const payload = buildDirectorPayload(documentRef.current, sourceSceneId);
      if (!payload) throw new Error("Scenen findes ikke");
      const row = await upsertDirectorFork(
        apiBase,
        projectId,
        sourceSceneId,
        episodeId,
        payload,
      );
      setForks((prev) => ({ ...prev, [sourceSceneId]: row.payload }));
      return row.payload;
    },
    [apiBase, episodeId, projectId],
  );

  const applyMutation = useCallback(
    (
      sourceSceneId: Id,
      mutate: (doc: ScriptDocument) => ScriptDocument,
    ): ScriptDocument | null => {
      const current = forksRef.current[sourceSceneId];
      if (!current) return null;
      if (current.submitState?.locked) return null;

      const base = documentRef.current;

      // Unlocked with live submission → edit the vault scene in the document.
      if (
        current.submitState &&
        hasActiveSubmission(base, sourceSceneId, current)
      ) {
        const submittedId = current.submitState.submittedSceneId!;
        const display = displayPayloadForFork(base, sourceSceneId, current);
        const merged = documentWithDirectorFork(base, sourceSceneId, display);
        const nextView = mutate(merged);
        const overlay = buildDirectorPayload(
          nextView,
          sourceSceneId,
          current.submitState,
        );
        if (!overlay) return null;
        const persistMutate = (doc: ScriptDocument) =>
          applyPayloadToScene(doc, submittedId, overlay);
        onDocumentMutateRef.current?.(persistMutate);
        const optimistic = persistMutate(base);
        setForks((prev) => ({
          ...prev,
          [sourceSceneId]: withSubmitState(overlay, current.submitState),
        }));
        // Keep fork payload in sync (content snapshot + submitState).
        schedulePush(
          sourceSceneId,
          withSubmitState(overlay, current.submitState),
        );
        return documentWithDirectorFork(
          optimistic,
          sourceSceneId,
          withSubmitState(overlay, current.submitState),
        );
      }

      // Normal fork editing (incl. after consumed submission).
      const merged = documentWithDirectorFork(base, sourceSceneId, current);
      const nextDoc = mutate(merged);
      const payload = buildDirectorPayload(
        nextDoc,
        sourceSceneId,
        current.submitState,
      );
      if (!payload) return null;
      setForks((prev) => ({ ...prev, [sourceSceneId]: payload }));
      schedulePush(sourceSceneId, payload);
      return nextDoc;
    },
    [schedulePush],
  );

  const replaceForkPayload = useCallback(
    (sourceSceneId: Id, payload: DirectorScenePayload) => {
      const prev = forksRef.current[sourceSceneId];
      const next = withSubmitState(payload, prev?.submitState);
      setForks((p) => ({ ...p, [sourceSceneId]: next }));
      schedulePush(sourceSceneId, next);
    },
    [schedulePush],
  );

  const getForkPayload = useCallback(
    (sourceSceneId: Id) => forksRef.current[sourceSceneId] ?? null,
    [],
  );

  const markSubmitted = useCallback(
    async (sourceSceneId: Id, submittedSceneId: Id) => {
      const current = forksRef.current[sourceSceneId];
      if (!current) return;
      const next = withSubmitState(current, {
        locked: true,
        submittedSceneId,
        submittedConsumed: false,
      });
      setForks((prev) => ({ ...prev, [sourceSceneId]: next }));
      await pushNow(sourceSceneId, next);
    },
    [pushNow],
  );

  const refreshArchives = useCallback(
    async (sourceSceneId: Id) => {
      if (!apiBase) return [];
      const rows = await listDirectorArchives(apiBase, projectId, sourceSceneId);
      setArchivesByScene((prev) => ({ ...prev, [sourceSceneId]: rows }));
      return rows;
    },
    [apiBase, projectId],
  );

  const unlockSceneSafe = useCallback(
    async (
      sourceSceneId: Id,
      options: { archive: boolean; label?: string },
    ) => {
      const current = forksRef.current[sourceSceneId];
      if (!current?.submitState?.locked) return;
      if (!apiBase || !episodeId) throw new Error("API mangler");

      if (options.archive) {
        const display = displayPayloadForFork(
          documentRef.current,
          sourceSceneId,
          current,
        );
        try {
          await createDirectorArchive(apiBase, projectId, sourceSceneId, {
            episodeId,
            mode: "new",
            label: options.label,
            payload: display,
          });
        } catch {
          // At max: try replaceAll so unlock is not blocked.
          await createDirectorArchive(apiBase, projectId, sourceSceneId, {
            episodeId,
            mode: "replaceAll",
            label: options.label,
            payload: display,
          });
        }
        await refreshArchives(sourceSceneId);
      }

      const consumed =
        Boolean(current.submitState.submittedConsumed) ||
        !hasActiveSubmission(documentRef.current, sourceSceneId, current);

      const next = withSubmitState(current, {
        ...current.submitState,
        locked: false,
        submittedConsumed: consumed,
        submittedSceneId: consumed
          ? null
          : current.submitState.submittedSceneId,
      });
      setForks((prev) => ({ ...prev, [sourceSceneId]: next }));
      await pushNow(sourceSceneId, next);
    },
    [apiBase, episodeId, projectId, pushNow, refreshArchives],
  );

  const archiveFork = useCallback(
    async (
      sourceSceneId: Id,
      options: {
        mode: ArchiveSceneMode;
        replaceArchiveId?: Id;
        label?: string;
      },
    ) => {
      if (!apiBase || !episodeId) throw new Error("API mangler");
      const payload = forksRef.current[sourceSceneId];
      if (!payload) throw new Error("Ingen egen version at arkivere");
      const display = displayPayloadForFork(
        documentRef.current,
        sourceSceneId,
        payload,
      );
      const row = await createDirectorArchive(
        apiBase,
        projectId,
        sourceSceneId,
        {
          episodeId,
          mode: options.mode,
          replaceArchiveId: options.replaceArchiveId,
          label: options.label,
          payload: display,
        },
      );
      await refreshArchives(sourceSceneId);
      return row;
    },
    [apiBase, episodeId, projectId, refreshArchives],
  );

  const purgeDirectorArchive = useCallback(
    async (sourceSceneId: Id, archiveId: Id) => {
      if (!apiBase) return;
      await deleteDirectorArchive(apiBase, projectId, archiveId);
      await refreshArchives(sourceSceneId);
    },
    [apiBase, projectId, refreshArchives],
  );

  const flushPending = useCallback(async () => {
    if (!apiBase || !episodeId) return;
    clearPushTimers();
    const entries = Object.entries(forksRef.current);
    await Promise.all(
      entries.map(([sourceSceneId, payload]) =>
        upsertDirectorFork(
          apiBase,
          projectId,
          sourceSceneId,
          episodeId,
          payload,
        ),
      ),
    );
  }, [apiBase, clearPushTimers, episodeId, projectId]);

  const getSubmitState = useCallback(
    (sceneId: Id) => forks[sceneId]?.submitState ?? null,
    [forks],
  );

  return {
    loading,
    error,
    forks,
    mergedDocument,
    hasFork,
    isSubmitLocked,
    editsSubmittedVault,
    getSubmitState,
    sceneRowClass,
    createFork,
    applyMutation,
    replaceForkPayload,
    getForkPayload,
    markSubmitted,
    unlockScene: unlockSceneSafe,
    archivesByScene,
    refreshArchives,
    archiveFork,
    purgeDirectorArchive,
    flushPending,
  };
}
